'use client';

import React from 'react';
import { useReducedMotion } from 'framer-motion';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ChartContainer, type ChartConfig } from '@/components/ui/chart';
import { cn } from '@/lib/utils';

import ChartTexture, { textureFill, textureMask, textureWash } from './ChartTexture';
import ChartTip from './ChartTip';
import { PRIMARY } from './palette';
import { ACTIVE_DOT, AXIS, GRID, HOVER_LINE, MARGIN } from './presets';

export interface TrendAreaProps<T extends Record<string, unknown>> {
  data: T[];
  /** Field plotted on the x axis (usually a date string). */
  xKey: string;
  /** Field plotted on the y axis. */
  yKey: string;
  /** Series name, used by the tooltip and screen readers. */
  label: string;
  color?: string;
  /** Halftone fill. Turn it off only for a chart printed at a tiny size. */
  textured?: boolean;
  showGrid?: boolean;
  /** Drop both axes for an inline, chrome-free trend. */
  hideAxes?: boolean;
  xTickFormatter?: (value: string) => string;
  yTickFormatter?: (value: number) => string;
  /** Formats the tooltip value. Defaults to the raw number. */
  valueFormatter?: (value: number) => string;
  /** Formats the tooltip label. Defaults to the raw x value. */
  labelFormatter?: (value: string) => string;
  className?: string;
}

/**
 * Canonical single-series trend: one brand-green area with the halftone fill,
 * horizontal grid only, and a crosshair tooltip. Use it for any "how did this
 * number move over time" chart, so they all read the same way.
 */
export default function TrendArea<T extends Record<string, unknown>>({
  data,
  xKey,
  yKey,
  label,
  color = PRIMARY,
  textured = true,
  showGrid = true,
  hideAxes = false,
  xTickFormatter,
  yTickFormatter,
  valueFormatter,
  labelFormatter,
  className,
}: TrendAreaProps<T>) {
  const reactId = React.useId();
  const id = reactId.replace(/:/g, '');
  const reduceMotion = useReducedMotion();

  const config = React.useMemo<ChartConfig>(
    () => ({ [yKey]: { label, color } }),
    [yKey, label, color]
  );

  return (
    <ChartContainer
      config={config}
      className={cn('aspect-auto h-full w-full', className)}
    >
      <AreaChart data={data} margin={MARGIN}>
        <defs>
          <ChartTexture id={id} color={color} />
        </defs>

        {showGrid ? <CartesianGrid {...GRID} /> : null}

        {hideAxes ? null : (
          <XAxis dataKey={xKey} {...AXIS} tickFormatter={xTickFormatter} />
        )}
        {hideAxes ? null : <YAxis {...AXIS} tickFormatter={yTickFormatter} />}

        <Tooltip
          cursor={HOVER_LINE}
          content={({ active, payload, label: tipLabel }) => {
            if (!active || !payload?.length) return null;
            const raw = payload[0]?.value;
            // A point with no value is not a zero. Coercing it printed "0" in
            // the tooltip, which reads as a measured result, so an unusable
            // value renders as the house placeholder instead.
            const numeric = typeof raw === 'number' ? raw : Number(raw);
            const hasValue = Number.isFinite(numeric);
            return (
              <ChartTip
                label={
                  labelFormatter
                    ? labelFormatter(String(tipLabel))
                    : String(tipLabel ?? '')
                }
                rows={[
                  {
                    key: yKey,
                    label,
                    value: !hasValue
                      ? '-'
                      : valueFormatter
                        ? valueFormatter(numeric)
                        : numeric.toLocaleString(),
                  },
                ]}
              />
            );
          }}
        />

        {/* Soft wash under the dots, so a thin area still registers. */}
        <Area
          type="monotone"
          dataKey={yKey}
          stroke="none"
          fill={textureWash(id)}
          isAnimationActive={!reduceMotion}
          activeDot={false}
        />
        <Area
          type="monotone"
          dataKey={yKey}
          stroke={color}
          strokeWidth={2}
          fill={textured ? textureFill(id) : textureWash(id)}
          mask={textured ? textureMask(id) : undefined}
          activeDot={ACTIVE_DOT}
          isAnimationActive={!reduceMotion}
        />
      </AreaChart>
    </ChartContainer>
  );
}
